import type {MouseEvent} from 'react';

import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';

import {useMapWalkSlider} from './useMapWalkSlider';
import {LocationOnIcon, PaymentsIcon} from 'Images/icons';

import type {WalkSliderCard} from './types';

const COLLAPSED_OPACITY = 0.7;
const COLLAPSED_HOVER_OPACITY = 0.9;
const COLLAPSED_BRIGHTNESS = 1.5;

/**
 * Stops a click from also bubbling to the tile's own select handler.
 *
 * @param e The click event.
 * @returns Nothing.
 */
const stopPropagation = (e: MouseEvent) => e.stopPropagation();

/** The currently clicked waypoint of the selected walk, shown inside its expanded card. */
export interface ActiveWaypoint {
    /** Optional waypoint description. */
    description?: string;
    /** Waypoint title. */
    title: string;
}

/**
 * The props for the `MapWalkSlider` component.
 * Configures a horizontal stack of walk tiles overlaid on the map, in a fixed order —
 * every tile already shows its full details; selecting one just brings it to full opacity/focus.
 */
interface ComponentProps {
    /** The currently clicked waypoint of the selected walk, if any. */
    activeWaypoint?: ActiveWaypoint | null;
    /** Called when the active waypoint's close button is tapped, to clear it. */
    onCloseWaypoint?(): void;
    /** Called with a walk's ID when a not-yet-selected tile is tapped to select it. */
    onSelectWalk(walkId: string): void;
    /** The ID of the currently selected walk, if any. */
    selectedWalkId: string | null;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'MapWalkSlider'
     */
    testId?: string;
    /** Walk tiles in a fixed order — selecting a walk never reorders this list. */
    walks: WalkSliderCard[];
}

/**
 * Renders a horizontal stack of walk tiles at the bottom of the map, in a fixed order.
 * Every tile already shows its full details (image, title, city, category tags, waypoint
 * count, price) — nothing is hidden behind selection. Tapping a not-yet-selected (dimmed)
 * tile selects it and scrolls the slider to wherever that tile already sits, without
 * reordering any tiles. The selected tile gains a "Details ansehen" link to its detail
 * page — tapping the tile itself never navigates, only that explicit link does. While a
 * marker on the map is clicked, the selected tile also gains an extra section with that
 * waypoint's title/description, integrating map selection feedback into the tile instead
 * of a separate overlay.
 * Only the tiles themselves capture pointer events — the space around them lets touches
 * and drags on the map underneath through, so panning the map still works.
 *
 * @param props                 The component props.
 * @param props.activeWaypoint  The currently clicked waypoint, if any.
 * @param props.onCloseWaypoint Called when the active waypoint's close button is tapped.
 * @param props.onSelectWalk    Called with a walk ID when a not-yet-selected tile is tapped.
 * @param props.selectedWalkId  The ID of the currently selected walk, if any.
 * @param props.testId          An optional test ID used for automation selectors. Defaults to `'MapWalkSlider'`.
 * @param props.walks           Walk tiles in a fixed order.
 * @returns A JSX element rendering the MapWalkSlider, or `null` when there are no walks.
 *
 * @example
 * ```tsx
 * <MapWalkSlider activeWaypoint={activeMarker} selectedWalkId={selectedWalkId} walks={walks} onSelectWalk={handleSelectWalk} />
 * ```
 */
const MapWalkSlider = ({
    activeWaypoint,
    onCloseWaypoint,
    onSelectWalk,
    selectedWalkId,
    testId = 'MapWalkSlider',
    walks
}: ComponentProps) => {
    const {sliderRef, t} = useMapWalkSlider(selectedWalkId, walks);

    if (walks.length === 0) return null;

    return (
        <Wrapper data-cy={testId}>
            <Track ref={sliderRef} className="keen-slider" data-cy={`${testId}-track`}>
                {walks.map(walk => {
                    const isSelected = walk.id === selectedWalkId;

                    return (
                        <Slide key={walk.id} className="keen-slider__slide">
                            <Card
                                $isSelected={isSelected}
                                data-cy={isSelected ? `${testId}-card-expanded` : `${testId}-card`}
                                role="button"
                                tabIndex={0}
                                onClick={() => onSelectWalk(walk.id)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onSelectWalk(walk.id);
                                    }
                                }}
                            >
                                {walk.imageUrl && (
                                    <ImageWrapper>
                                        <ResponsiveImage
                                            alt={walk.imageAlt ?? walk.title}
                                            sizes="27rem"
                                            src={walk.imageUrl}
                                        />
                                    </ImageWrapper>
                                )}
                                <Content>
                                    <Title>{walk.title}</Title>
                                    <City>{walk.city}</City>
                                    {walk.categories.length > 0 && (
                                        <TagsRow data-cy={`${testId}-tags`}>
                                            {walk.categories.map(cat => (
                                                <Tag key={cat.id}>{cat.name}</Tag>
                                            ))}
                                        </TagsRow>
                                    )}
                                    <MetaRow>
                                        <MetaItem>
                                            <LocationOnIcon height={14} width={14} />
                                            <MetaText>{walk.waypointCountLabel}</MetaText>
                                        </MetaItem>
                                        <MetaItem $accent>
                                            <PaymentsIcon height={14} width={14} />
                                            <MetaText>{walk.priceLabel}</MetaText>
                                        </MetaItem>
                                    </MetaRow>
                                    {isSelected && activeWaypoint && (
                                        <WaypointSection data-cy={`${testId}-waypoint-info`}>
                                            <WaypointHeader>
                                                <WaypointTitle>{activeWaypoint.title}</WaypointTitle>
                                                <WaypointCloseButton
                                                    aria-label={t('walkMap.close')}
                                                    data-cy={`${testId}-waypoint-close`}
                                                    type="button"
                                                    onClick={e => {
                                                        stopPropagation(e);
                                                        onCloseWaypoint?.();
                                                    }}
                                                >
                                                    ✕
                                                </WaypointCloseButton>
                                            </WaypointHeader>
                                            {activeWaypoint.description && (
                                                <WaypointDescription>{activeWaypoint.description}</WaypointDescription>
                                            )}
                                        </WaypointSection>
                                    )}
                                    {isSelected && (
                                        <DetailsLink href={`/walks/${walk.slug}`} onClick={stopPropagation}>
                                            {t('walkMap.detailsLink')}
                                        </DetailsLink>
                                    )}
                                </Content>
                            </Card>
                        </Slide>
                    );
                })}
            </Track>
        </Wrapper>
    );
};

MapWalkSlider.displayName = 'MapWalkSlider';

export {MapWalkSlider};

// ── Styled ──────────────────────────────────────────────────

/*
 * `Wrapper`/`Track` span the full map width as a layout box, but stay click-through
 * (`pointer-events: none`) so drag gestures on the map reach it everywhere except directly
 * over a tile — only the tile itself opts back in via `pointer-events: auto`.
 */
const Wrapper = styled.div`
    bottom: ${spacing(4)};
    left: 0;
    padding: 0 ${spacing(4)};
    pointer-events: none;
    position: absolute;
    right: 0;
    z-index: 950;
`;

/*
 * Tiles are (roughly) uniform height since every tile shows the same set of details, so no
 * fixed shelf height is needed — the row just sizes to its tallest tile. Bottom-aligned
 * (not top) so a selected tile that grows slightly taller (extra waypoint section) still
 * shares the same baseline with its neighbors, and `overflow: visible` keeps that growth
 * from being clipped — the outer `WalkMap` container still clips it at the map's own bounds.
 */
const Track = styled.div`
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    align-items: flex-end;
    display: flex;
    overflow: visible;
    touch-action: pan-y;
    user-select: none;
`;

const Slide = styled.div`
    flex-shrink: 0;
    overflow: visible;
    width: 20rem;

    ${media('sm')} {
        width: 25rem;
    }
`;

interface SelectableProps {
    /** Whether this tile is the selected one. */
    $isSelected: boolean;
}

/*
 * One tile element for every walk — every tile already shows its full details (image,
 * title, city, tags, meta row); selection only changes opacity/focus, an optional extra
 * waypoint section, and the "Details ansehen" link. Text is never truncated — the tile
 * simply grows as tall as its content needs. Styling (flat tonal surface, rounded top-only
 * image, pill tags, meta icon rows) blends `TourListItem`'s tonal/meta-row language with
 * `WalkCard`'s full-width top image.
 */
const Card = styled.div<SelectableProps>`
    background: ${({theme}) => theme.colors.surfaceLow};
    border-radius: 1.6rem;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    opacity: ${({$isSelected}) => ($isSelected ? 1 : COLLAPSED_OPACITY)};
    overflow: hidden;
    pointer-events: auto;
    text-align: left;
    transition: background 0.2s ease, opacity 0.2s ease;

    &:hover {
        background: ${({theme}) => theme.colors.surfaceMid};
        opacity: ${({$isSelected}) => ($isSelected ? 1 : COLLAPSED_HOVER_OPACITY)};
    }

    img {
        filter: grayscale(${({$isSelected}) => ($isSelected ? 0 : 1)})
            brightness(${({$isSelected}) => ($isSelected ? 1 : COLLAPSED_BRIGHTNESS)});
        transition: filter 0.5s;
    }
`;

const ImageWrapper = styled.div`
    height: 9.6rem;
    overflow: hidden;
    width: 100%;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding: ${spacing(3)};
`;

const Title = styled.h3`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
`;

const City = styled.span`
    color: ${({theme}) => theme.colors.inkTertiary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 500;
`;

const TagsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(1)};
    margin-top: ${spacing(1)};
`;

const Tag = styled.span`
    background: ${({theme}) => theme.colors.white};
    border-radius: 9999px;
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1rem;
    font-weight: 600;
    padding: 0.2rem ${spacing(2)};
`;

const MetaRow = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(3)};
    margin-top: ${spacing(2)};
`;

interface MetaItemProps {
    /** Whether to use the primary accent color (used for the price). */
    $accent?: boolean;
}

const MetaItem = styled.span<MetaItemProps>`
    align-items: center;
    color: ${({$accent, theme}) => ($accent ? theme.colors.primaryDefault : theme.colors.inkTertiary)};
    display: flex;
    gap: ${spacing(1)};
`;

const MetaText = styled.span`
    color: inherit;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.1rem;
    font-weight: 500;
`;

/* The "extra section" shown only while a marker on the map is selected — a nested panel, set off from the tonal tile by a white background. */
const WaypointSection = styled.div`
    background: ${({theme}) => theme.colors.white};
    border-radius: 1rem;
    margin-top: ${spacing(2)};
    padding: ${spacing(2)} ${spacing(3)};
`;

const WaypointHeader = styled.div`
    align-items: flex-start;
    display: flex;
    gap: ${spacing(2)};
    justify-content: space-between;
`;

const WaypointTitle = styled.p`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0;
`;

const WaypointCloseButton = styled.button`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceLow};
    border: none;
    border-radius: 50%;
    color: ${({theme}) => theme.colors.inkSecondary};
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    font-size: 1rem;
    height: 1.8rem;
    justify-content: center;
    width: 1.8rem;
`;

const WaypointDescription = styled.p`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    line-height: 1.35;
    margin: 0.3rem 0 0;
`;

/* Only rendered on the selected tile — the sole way to navigate to the walk's detail page. */
const DetailsLink = styled(Link)`
    align-self: flex-start;
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.2rem;
    font-weight: 700;
    margin-top: ${spacing(2)};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;